import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheck,
  FaClipboardCheck,
  FaClock,
  FaDownload,
  FaFileAlt,
  FaFileArchive,
  FaFilePdf,
  FaHandshake,
  FaMapMarkerAlt,
  FaPhone,
  FaShip,
  FaShieldAlt,
  FaTruck,
} from "react-icons/fa";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { transformManifestData } from "../../utils/shippingManifestView";
import "../../assets/styles/logistics_shipment_transit.scss";

function formatDateTime(v) {
  if (v == null || String(v).trim() === "") return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatDateOnly(v) {
  if (v == null || String(v).trim() === "") return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function rawStatusNorm(m) {
  return String(m?.status || "")
    .trim()
    .toLowerCase();
}

function documentTypeLabel(t) {
  const k = String(t || "")
    .trim()
    .toLowerCase();
  const map = {
    bill_of_lading: "Bill of Lading",
    packing_list: "Packing List",
    invoice: "Commercial Invoice",
    certificate_of_origin: "Certificate of Origin",
    eway_bill: "E-Way Bill",
    quality_certificate: "Quality Certificate",
    other_document: "Other Documents",
    pod: "Proof of Delivery",
    proof_of_delivery: "Proof of Delivery",
  };
  return map[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Document";
}

function docIconClass(type) {
  const k = String(type || "").trim().toLowerCase();
  if (k === "certificate_of_origin") return "lst-doc-card__icon--purple";
  if (k === "packing_list") return "lst-doc-card__icon--orange";
  if (k === "pod" || k === "proof_of_delivery") return "lst-doc-card__icon--red";
  if (k === "quality_certificate") return "lst-doc-card__icon--green";
  if (k === "other_document") return "lst-doc-card__icon--brown";
  return "lst-doc-card__icon--blue";
}

function formatRfqTypeShort(kind) {
  const k = String(kind || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  if (k === "generic" || k === "generic-logistics") return "Generic";
  if (k === "exhibition-logistics") return "Exhibition";
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : "—";
}

function formatTransportMode(mode) {
  const m = String(mode || "").trim();
  if (!m || m === "N/A") return "—";
  if (/sea/i.test(m)) return "Sea Freight";
  if (/air/i.test(m)) return "Air Freight";
  if (/road/i.test(m)) return "Road Freight";
  return m;
}

function buildTimeline(vm, raw) {
  const originLoc = [raw?.from_city, raw?.from_state, raw?.from_country].filter(Boolean).join(", ") || "—";
  const portLoc = raw?.preferred_delivery_port || vm?.deliveryPort || "—";
  const routeLoc = raw?.route || "At sea / en route";
  const destLoc = [raw?.to_city, raw?.to_state, raw?.to_country].filter(Boolean).join(", ") || "—";
  const st = rawStatusNorm(raw);
  const dep = raw?.departure_date || null;
  const expArr = raw?.expected_arrival_date || raw?.estimated_delivery_date || vm?.expectedArrival || null;
  const arrived = st === "arrived";

  let activeIdx = 3;
  if (arrived) activeIdx = 4;

  const defs = [
    {
      key: "handover",
      title: "Goods handed over",
      location: originLoc,
      timeLabel: formatDateTime(raw?.manifest_date || raw?.created_datetime),
    },
    {
      key: "loaded",
      title: "Container loaded",
      location: portLoc,
      timeLabel: dep ? formatDateTime(dep) : "—",
    },
    {
      key: "departed",
      title: "Vessel departed",
      location: portLoc,
      timeLabel: dep ? formatDateTime(dep) : "—",
    },
    {
      key: "transit",
      title: "In transit",
      location: routeLoc,
      timeLabel: formatDateTime(raw?.updated_datetime || raw?.accepted_date || raw?.created_datetime),
    },
    {
      key: "arrival",
      title: "Arrival at port",
      location: destLoc,
      timeLabel: expArr ? `Expected: ${formatDateOnly(expArr)}` : "Pending",
    },
    {
      key: "customs",
      title: "Customs clearance",
      location: destLoc,
      timeLabel: "Pending",
    },
    {
      key: "delivery",
      title: "Out for delivery",
      location: destLoc,
      timeLabel: "Pending",
    },
  ];

  return defs.map((d, i) => {
    let state = "pending";
    if (i < activeIdx) state = "done";
    else if (i === activeIdx) state = "active";
    return { ...d, state };
  });
}

function timelineProgressPercent(items) {
  const done = items.filter((x) => x.state === "done").length;
  const active = items.some((x) => x.state === "active");
  const pct = Math.round(((done + (active ? 0.45 : 0)) / items.length) * 100);
  return Math.min(100, Math.max(0, pct));
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  } catch {
    return null;
  }
}

const TOP_STEPS = [
  { id: "handover", label: "Handover", Icon: FaHandshake },
  { id: "manifest", label: "Shipping manifest", Icon: FaFileAlt },
  { id: "transit", label: "In transit", Icon: FaTruck },
  { id: "grn", label: "GRN", Icon: FaClipboardCheck },
];

function DetailCell({ label, value, dotGreen }) {
  return (
    <div className="lst-detail-cell">
      <div className="lst-detail-cell__label">{label}</div>
      <div className={`lst-detail-cell__value ${dotGreen ? "lst-detail-cell__value--dot" : ""}`}>
        {dotGreen ? <span className="lst-dot lst-dot--green" aria-hidden /> : null}
        {value}
      </div>
    </div>
  );
}

export default function LogisticsShipmentTransitPage() {
  const { submissionId, manifestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const sid = submissionId != null ? Number(submissionId) : NaN;
  const mid = manifestId != null ? Number(manifestId) : NaN;

  const [loading, setLoading] = useState(true);
  const [rawManifest, setRawManifest] = useState(null);
  const [viewModel, setViewModel] = useState(null);
  const [rfqMeta, setRfqMeta] = useState(null);
  const [handoverSnap, setHandoverSnap] = useState(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(mid) || mid <= 0) {
      toast.error("Invalid manifest");
      navigate("/dashboard/logistics/rfqs", { replace: true });
      return;
    }
    try {
      setLoading(true);
      const manifestRes = await CommonService.get(`/shipping-manifest/${mid}`);
      const raw = manifestRes?.data;
      if (!raw) throw new Error(manifestRes?.message || "Manifest not found");
      const st = rawStatusNorm(raw);
      const accepted = Boolean(raw?.accepted_date || st === "accepted");
      if (!accepted) {
        toast.info("Shipment tracking is available after the manifest is accepted.");
        navigate("/dashboard/logistics/rfqs", { replace: true });
        return;
      }
      const vm = transformManifestData(raw);
      if (!vm) throw new Error("Failed to map manifest");

      let rfq = null;
      let handover = null;
      const quoteId = raw.logistics_quote_id != null ? Number(raw.logistics_quote_id) : NaN;
      const subId = Number.isFinite(sid) && sid > 0 ? sid : raw.logistics_generic_rfq_id;

      if (Number.isFinite(subId) && subId > 0) {
        try {
          const rfqRes = await CommonService.get(`/company/logistics-providers/rfq/submission/${subId}`);
          const body = rfqRes?.data ?? rfqRes ?? {};
          rfq = body?.data ?? body?.rfq ?? body;
        } catch {
          rfq = null;
        }
      }

      if (Number.isFinite(subId) && subId > 0 && Number.isFinite(quoteId) && quoteId > 0) {
        try {
          const ctxRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_PICKUP_CONTEXT(subId, quoteId));
          const ctxBody = ctxRes?.data ?? ctxRes ?? {};
          const ctx = ctxBody?.data ?? ctxBody;
          handover = ctx?.handover?.handover ?? null;
        } catch {
          handover = null;
        }
      }

      setRawManifest(raw);
      setViewModel(vm);
      setRfqMeta(rfq);
      setHandoverSnap(handover);

      const syncSubId =
        Number.isFinite(sid) && sid > 0
          ? sid
          : raw.logistics_generic_rfq_id != null
            ? Number(raw.logistics_generic_rfq_id)
            : NaN;
      if (Number.isFinite(syncSubId) && syncSubId > 0 && Number.isFinite(quoteId) && quoteId > 0) {
        try {
          await CommonService.post(
            API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_COMPLETE_DELIVERY_STAGE(syncSubId, quoteId),
            { stageKey: "transit" },
          );
        } catch (syncErr) {
          console.warn(
            "Could not sync logistics transit delivery stage",
            syncErr?.response?.data?.message || syncErr?.message,
          );
        }
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to load shipment";
      toast.error(msg);
      navigate("/dashboard/logistics/rfqs", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [mid, sid, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const timeline = useMemo(() => {
    if (!viewModel || !rawManifest) return [];
    return buildTimeline(viewModel, rawManifest);
  }, [viewModel, rawManifest]);

  const progressPct = useMemo(() => timelineProgressPercent(timeline), [timeline]);

  const stepStates = useMemo(() => {
    const st = rawManifest ? rawStatusNorm(rawManifest) : "";
    const arrived = st === "arrived";
    return {
      handover: "done",
      manifest: "done",
      transit: arrived ? "done" : "active",
      grn: arrived ? "active" : "pending",
    };
  }, [rawManifest]);

  const stepProgressPct = useMemo(() => {
    const st = rawManifest ? rawStatusNorm(rawManifest) : "";
    if (st === "arrived") return 88;
    return 62;
  }, [rawManifest]);

  const rfqRef =
    location.state?.rfqReferenceNumber ??
    rfqMeta?.rfqReferenceNumber ??
    rfqMeta?.rfq_reference_number ??
    "—";

  const rfqPayload = rfqMeta?.payload && typeof rfqMeta.payload === "object" ? rfqMeta.payload : {};
  const cargo =
    rfqPayload.commodityType || rfqPayload.cargoDescription || rfqPayload.materialsDescription || "—";
  const rfqKind = rfqMeta?.rfqKind ?? rfqMeta?.rfq_kind ?? null;

  const currentLocationLabel = useMemo(() => {
    if (!viewModel) return "—";
    const port = viewModel.deliveryPort && viewModel.deliveryPort !== "N/A" ? viewModel.deliveryPort : null;
    if (port) return port;
    return viewModel.route || "At sea / en route";
  }, [viewModel]);

  const etaLabel = formatDateOnly(
    rawManifest?.expected_arrival_date || rawManifest?.estimated_delivery_date || viewModel?.expectedArrival,
  );

  const daysLeft = daysUntil(
    rawManifest?.expected_arrival_date || rawManifest?.estimated_delivery_date || viewModel?.expectedArrival,
  );

  const documents = useMemo(() => {
    const docs = Array.isArray(rawManifest?.documents) ? rawManifest.documents : [];
    return docs
      .filter((d) => d?.document_path)
      .map((d) => ({
        id: d.id,
        type: d.document_type,
        label: documentTypeLabel(d.document_type),
        name: d.document_name || documentTypeLabel(d.document_type),
        href: resolvePublicFileUrl(d.document_path),
        ext: String(d.document_name || "").toLowerCase().endsWith(".zip") ? "ZIP" : "PDF",
      }));
  }, [rawManifest]);

  const vesselLabel = useMemo(() => {
    const carrier = rawManifest?.carrier_vessel || viewModel?.carrier;
    const vessel = rawManifest?.vessel_flight_number || viewModel?.vesselOrFlight;
    if (carrier && vessel) return `${carrier} (${vessel})`;
    return carrier || vessel || "—";
  }, [rawManifest, viewModel]);

  const bolNumber = rawManifest?.bill_of_lading || viewModel?.billOfLading || "—";
  const sealNumber = handoverSnap?.sealNumber || rawManifest?.seal_number || "—";
  const contactName = handoverSnap?.contactPersonName || "—";
  const contactPhone = handoverSnap?.contactPhone || "—";

  const transitProgressPct = useMemo(() => {
    if (!rawManifest) return 72;
    const st = rawStatusNorm(rawManifest);
    if (st === "arrived") return 100;
    const dep = rawManifest.departure_date;
    const eta = rawManifest.expected_arrival_date || rawManifest.estimated_delivery_date;
    if (dep && eta) {
      const start = new Date(dep).getTime();
      const end = new Date(eta).getTime();
      const now = Date.now();
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
        return Math.min(95, Math.max(8, Math.round(((now - start) / (end - start)) * 100)));
      }
    }
    return Math.max(progressPct, 72);
  }, [rawManifest, progressPct]);

  const mapStats = useMemo(() => {
    const totalKm = 10000;
    const covered = Math.round((totalKm * transitProgressPct) / 100);
    return {
      distance: `${covered.toLocaleString()} km`,
      distancePct: transitProgressPct,
      daysLeft: daysLeft != null ? `${daysLeft} days` : "—",
      eta: etaLabel,
      location: viewModel?.route || "At sea / en route",
      speed: "22.5 km/h",
    };
  }, [transitProgressPct, daysLeft, etaLabel, viewModel]);

  if (loading || !viewModel || !rawManifest) {
    return (
      <div className="lst-page">
        <div className="container-fluid py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lst-page">
      <div className="container-fluid py-4">
        <div className="lst-inner">
          <nav aria-label="breadcrumb" className="lst-breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/dashboard/logistics">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/dashboard/logistics/rfqs">RFQs received</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Shipment tracking
              </li>
            </ol>
          </nav>

          <div className="lst-hero">
            <div>
              <h1 className="lst-hero__title">Shipment tracking</h1>
              <p className="lst-hero__subtitle">Real-time tracking and status updates of your shipment.</p>
            </div>
            <Link to="/dashboard/logistics/rfqs" className="lst-btn-back">
              <FaArrowLeft aria-hidden />
              Back to RFQs
            </Link>
          </div>

          <div className="lst-stepper-wrap">
            <div className="lst-stepper" role="list">
              <div className="lst-stepper__track" aria-hidden>
                <div className="lst-stepper__fill" style={{ width: `${stepProgressPct}%` }} />
              </div>
              {TOP_STEPS.map((step) => {
                const state = stepStates[step.id] || "pending";
                const { Icon } = step;
                return (
                  <div key={step.id} className={`lst-step lst-step--${state}`} role="listitem">
                    <div className="lst-step__circle">
                      {state === "done" ? <FaCheck aria-hidden /> : <Icon aria-hidden />}
                    </div>
                    <div className="lst-step__label">{step.label}</div>
                    {state === "done" ? <div className="lst-step__sub lst-step__sub--done">Completed</div> : null}
                    {state === "active" && step.id === "transit" ? (
                      <div className="lst-step__sub lst-step__sub--active">Current step</div>
                    ) : null}
                    {state === "pending" ? <div className="lst-step__sub">Pending</div> : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lst-status-banner">
            <div className="lst-status-banner__icon" aria-hidden>
              <FaTruck />
            </div>
            <div className="lst-status-banner__grid">
              <div>
                <div className="lst-status-banner__k">Current status</div>
                <div className="lst-status-banner__main">In transit</div>
              </div>
              <div>
                <div className="lst-status-banner__k">
                  <FaMapMarkerAlt aria-hidden /> Location
                </div>
                <div className="lst-status-banner__v">{currentLocationLabel}</div>
              </div>
              <div>
                <div className="lst-status-banner__k">
                  <FaCalendarAlt aria-hidden /> ETA
                </div>
                <div className="lst-status-banner__v">
                  {etaLabel}
                  {etaLabel !== "—" ? " (Expected delivery)" : ""}
                </div>
              </div>
              <div>
                <div className="lst-status-banner__k">
                  <FaClock aria-hidden /> Last updated
                </div>
                <div className="lst-status-banner__v">{formatDateTime(viewModel.lastUpdated)}</div>
              </div>
            </div>
          </div>

          <div className="lst-details-grid">
            <DetailCell label="RFQ Reference" value={rfqRef} />
            <DetailCell label="Submission #" value={Number.isFinite(sid) && sid > 0 ? `#${sid}` : "—"} />
            <DetailCell label="Cargo (from RFQ)" value={cargo} />
            <DetailCell label="RFQ Type" value={formatRfqTypeShort(rfqKind)} />
            <DetailCell label="Total Packages" value={rawManifest.total_packages ?? viewModel.packages ?? "—"} />
            <DetailCell
              label="Total Weight"
              value={
                rawManifest.total_weight_kg != null
                  ? `${Number(rawManifest.total_weight_kg).toLocaleString()} KG`
                  : viewModel.weight?.replace(/ kg/i, " KG") || "—"
              }
            />
            <DetailCell label="Mode of Transport" value={formatTransportMode(viewModel.mode)} />
            <DetailCell label="Quote Status" value="Accepted" dotGreen />
          </div>

          <div className="lst-tracking-row">
            <div className="lst-card">
              <div className="lst-card__title">Shipment timeline</div>
              <div className="lst-timeline">
                {timeline.map((ev) => (
                  <div
                    key={ev.key}
                    className={`lst-tl-item lst-tl-item--${ev.state}`}
                  >
                    <span className="lst-tl-line" aria-hidden />
                    <div className="lst-tl-dot">
                      {ev.state === "done" ? (
                        <FaCheck aria-hidden />
                      ) : ev.state === "active" ? (
                        <FaTruck aria-hidden />
                      ) : (
                        <span className="lst-tl-dot__empty" />
                      )}
                    </div>
                    <div className="lst-tl-body">
                      <div className="lst-tl-title-row">
                        <span className="lst-tl-title">{ev.title}</span>
                        {ev.state === "active" ? (
                          <span className="lst-tl-badge">Current step</span>
                        ) : null}
                      </div>
                      <div className="lst-tl-meta">{ev.location}</div>
                      <div className="lst-tl-meta">{ev.timeLabel}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lst-card lst-card--map">
              <div className="lst-card__title">Live shipment map</div>
              <div className="lst-map" aria-hidden>
                <div className="lst-map__bg" />
                <svg className="lst-map__route" viewBox="0 0 400 200" preserveAspectRatio="none">
                  <path
                    d="M 40 80 Q 120 40 200 90 T 360 120"
                    fill="none"
                    stroke="#ff6a00"
                    strokeWidth="3"
                    strokeDasharray="8 6"
                  />
                </svg>
                <FaMapMarkerAlt className="lst-map__pin lst-map__pin--dest" />
                <FaShip className="lst-map__ship" />
              </div>
              <div className="lst-map-stats">
                <div className="lst-map-stat">
                  <div className="lst-map-stat__label">Distance covered</div>
                  <div className="lst-map-stat__value lst-map-stat__value--green">
                    {mapStats.distance} ({mapStats.distancePct}%)
                  </div>
                </div>
                <div className="lst-map-stat">
                  <div className="lst-map-stat__label">Est. time remaining</div>
                  <div className="lst-map-stat__value lst-map-stat__value--orange">
                    {mapStats.daysLeft}
                    {mapStats.eta !== "—" ? ` (${mapStats.eta})` : ""}
                  </div>
                </div>
                <div className="lst-map-stat">
                  <div className="lst-map-stat__label">Current location</div>
                  <div className="lst-map-stat__value lst-map-stat__value--blue">{mapStats.location}</div>
                </div>
                <div className="lst-map-stat">
                  <div className="lst-map-stat__label">Avg. speed</div>
                  <div className="lst-map-stat__value">{mapStats.speed}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lst-info-row">
            <div className="lst-info-card">
              <div className="lst-info-card__head">
                <span className="lst-info-card__icon lst-info-card__icon--blue">
                  <FaShip aria-hidden />
                </span>
                Vehicle / Vessel
              </div>
              <div className="lst-info-card__value">{vesselLabel}</div>
              <button type="button" className="lst-info-card__action" aria-label="Download vessel details">
                <FaDownload aria-hidden />
              </button>
            </div>
            <div className="lst-info-card">
              <div className="lst-info-card__head">
                <span className="lst-info-card__icon lst-info-card__icon--orange">
                  <FaFileAlt aria-hidden />
                </span>
                Bill of Lading
              </div>
              <div className="lst-info-card__value">{bolNumber}</div>
              <button type="button" className="lst-info-card__link">
                View details
              </button>
            </div>
            <div className="lst-info-card">
              <div className="lst-info-card__head">
                <span className="lst-info-card__icon lst-info-card__icon--green">
                  <FaShieldAlt aria-hidden />
                </span>
                Seal Number
              </div>
              <div className="lst-info-card__value">
                {sealNumber}
                {sealNumber !== "—" ? <span className="lst-verified"> (Verified)</span> : null}
              </div>
            </div>
            <div className="lst-info-card">
              <div className="lst-info-card__head">
                <span className="lst-info-card__icon lst-info-card__icon--purple">
                  <FaPhone aria-hidden />
                </span>
                Contact
              </div>
              <div className="lst-info-card__value">{contactName}</div>
              {contactPhone !== "—" ? <div className="lst-info-card__sub">{contactPhone}</div> : null}
            </div>
          </div>

          <div className="lst-card">
            <div className="lst-card__title">Available documents</div>
            {documents.length === 0 ? (
              <p className="lst-empty-docs">No documents uploaded for this manifest yet.</p>
            ) : (
              <div className="lst-docs-grid">
                {documents.map((doc) => (
                  <a
                    key={doc.id ?? doc.href}
                    href={doc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lst-doc-card"
                  >
                    <span className={`lst-doc-card__icon ${docIconClass(doc.type)}`}>
                      {doc.ext === "ZIP" ? <FaFileArchive aria-hidden /> : <FaFilePdf aria-hidden />}
                    </span>
                    <span className="lst-doc-card__text">
                      <span className="lst-doc-card__title">{doc.label}</span>
                      <span className="lst-doc-card__ext">{doc.ext}</span>
                    </span>
                    <FaDownload className="lst-doc-card__dl" aria-hidden />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="lst-support-banner">
            For any queries related to this shipment, please contact our support team.
          </div>
        </div>
      </div>
    </div>
  );
}
